import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { EMBEDDED_CSS, FONT, FluidStatusTabs, KpiBento, DmsHeader } from '../dmsTheme';
import DistributorSkuMasterTable from '../components/DistributorSkuMasterTable';

const DISTRIBUTOR = 'Choudhary Agencies';

const STATUS_COLORS = {
    OUT:        { bg:'#FEE2E2', color:'#991B1B', label:'Out of Stock' },
    CRITICAL:   { bg:'#FEE2E2', color:'#991B1B', label:'Critical (<10d)' },
    LOW:        { bg:'#FEF3C7', color:'#92400E', label:'Low (10-20d)' },
    HEALTHY:    { bg:'#D1FAE5', color:'#065F46', label:'Healthy (20d+)' },
    NON_MOVING: { bg:'#E5E7EB', color:'#374151', label:'Non-Moving' },
};

const TABS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'projection', label: '20-Day Projection' },
    { key: 'stock', label: 'Stock Status' },
    { key: 'sales', label: 'Sales Detail' },
    { key: 'sku_master', label: 'SKU Master' },
];

function exportXLSX(headers, dataRows, filename) {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    ws['!cols'] = headers.map(h => ({ wch: Math.max(String(h).length + 2, 14) }));
    XLSX.writeFile(wb, filename);
}

function today() { return new Date().toISOString().slice(0, 10); }

export default function ChoudharyInventory({ user }) {
    const canEdit = hasEditAccess(user, 'dms', 'choudhary_inventory');
    const [activeTab, setActiveTab] = useState('dashboard');
    const [insights, setInsights] = useState(null);
    const [stockRows, setStockRows] = useState([]);
    const [projection, setProjection] = useState(null);
    const [salesRows, setSalesRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadMsgs, setUploadMsgs] = useState({});
    const [uploadingKey, setUploadingKey] = useState(null);
    const [stockSearch, setStockSearch] = useState('');
    const [projSearch, setProjSearch] = useState('');
    const [projFilter, setProjFilter] = useState('all');
    const [salesSearch, setSalesSearch] = useState('');

    const loadStock = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/dms?action=stock&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setStockRows(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    const loadInsights = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/choudhary-inventory?action=insights`);
            const data = await res.json();
            setInsights(data);
        } catch (e) { console.error(e); }
    }, []);

    const loadProjection = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/choudhary-inventory?action=projection`);
            const data = await res.json();
            setProjection(data);
        } catch (e) { console.error(e); }
    }, []);

    const loadSales = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/dms?action=sales&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setSalesRows(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { loadStock(); loadInsights(); loadProjection(); loadSales(); }, [loadStock, loadInsights, loadProjection, loadSales]);

    async function handleUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingKey('stock'); setUploadMsgs((m) => ({ ...m, stock: null }));
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await fetch(`${API_BASE}/choudhary-inventory?action=upload`, { method: 'POST', body: form });
            const data = await res.json();
            if (res.ok && data.success) {
                setUploadMsgs((m) => ({ ...m, stock: { type: 'success', text: `Uploaded ${data.inserted} rows for ${formatDate(data.report_date)}${data.needs_mapping?.length ? ` · ${data.needs_mapping.length} unmapped (fix in SKU Master)` : ''}` } }));
                loadStock();
                loadProjection();
            } else {
                setUploadMsgs((m) => ({ ...m, stock: { type: 'error', text: data.message || 'Upload failed' } }));
            }
        } catch (err) {
            setUploadMsgs((m) => ({ ...m, stock: { type: 'error', text: 'Network error' } }));
        } finally { setUploadingKey(null); }
        e.target.value = '';
    }

    async function handleUploadSales(e) {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingKey('sales'); setUploadMsgs((m) => ({ ...m, sales: null }));
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await fetch(`${API_BASE}/choudhary-inventory?action=upload_sales&uploaded_by=${encodeURIComponent(user?.username || 'Portal')}`, { method: 'POST', body: form });
            const data = await res.json();
            if (res.ok && data.success) {
                setUploadMsgs((m) => ({ ...m, sales: { type: 'success', text: `${data.inserted} rows uploaded for ${data.month_year}${data.needs_mapping?.length ? ` · ${data.needs_mapping.length} unmapped (fix in SKU Master)` : ''}` } }));
                loadInsights();
                loadProjection();
                loadSales();
            } else {
                setUploadMsgs((m) => ({ ...m, sales: { type: 'error', text: data.message || 'Upload failed' } }));
            }
        } catch (err) {
            setUploadMsgs((m) => ({ ...m, sales: { type: 'error', text: 'Network error' } }));
        } finally { setUploadingKey(null); }
        e.target.value = '';
    }

    const latestReportDate = stockRows.length ? stockRows[0].report_date : null;
    const totalStockQty = stockRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const totalStockValue = stockRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const zeroStockSkus = stockRows.filter(r => Number(r.quantity) === 0).length;

    const filteredStock = stockRows.filter(r =>
        !stockSearch ||
        r.item_name?.toLowerCase().includes(stockSearch.toLowerCase()) ||
        r.ean?.toLowerCase().includes(stockSearch.toLowerCase())
    );

    const projRows = projection?.rows || [];
    const filteredProj = projRows
        .filter(r => projFilter === 'all' || r.status === projFilter)
        .filter(r => !projSearch || r.item_name?.toLowerCase().includes(projSearch.toLowerCase()));

    const filteredSales = salesRows.filter(r =>
        !salesSearch ||
        r.item_name?.toLowerCase().includes(salesSearch.toLowerCase()) ||
        r.particulars?.toLowerCase().includes(salesSearch.toLowerCase()) ||
        r.vch_bill_no?.toLowerCase().includes(salesSearch.toLowerCase())
    );

    return (
        <div className="dms-root" style={{ maxWidth: 1400, margin: '0 auto', background: 'var(--grn-page-bg)' }}>
            <style>{EMBEDDED_CSS}</style>
            <DmsHeader
                title="Choudhary" accent="Agencies"
                subtitle={`${latestReportDate ? `Stock as on ${formatDate(latestReportDate)} · ` : ''}Data shown is for the current month`}
                right={<>
                    {uploadMsgs.stock && (
                        <div style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: uploadMsgs.stock.type === 'success' ? 'rgba(92,126,96,0.25)' : 'rgba(194,94,119,0.25)', color: uploadMsgs.stock.type === 'success' ? '#B8E0BC' : '#F3B8C4' }}>
                            {uploadMsgs.stock.text}
                        </div>
                    )}
                    {uploadMsgs.sales && (
                        <div style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: uploadMsgs.sales.type === 'success' ? 'rgba(92,126,96,0.25)' : 'rgba(194,94,119,0.25)', color: uploadMsgs.sales.type === 'success' ? '#B8E0BC' : '#F3B8C4' }}>
                            {uploadMsgs.sales.text}
                        </div>
                    )}
                    <label style={{ padding: '9px 18px', background: 'var(--grn-purple)', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {uploadingKey === 'stock' ? 'Uploading...' : 'Upload Stock File'}
                        <input type="file" accept=".xls,.xlsx" style={{ display: 'none' }} onChange={handleUpload} disabled={!!uploadingKey} />
                    </label>
                    <label style={{ padding: '9px 18px', background: '#fff', color: '#0B0A12', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {uploadingKey === 'sales' ? 'Uploading...' : 'Upload Sales File'}
                        <input type="file" accept=".xls,.xlsx" style={{ display: 'none' }} onChange={handleUploadSales} disabled={!!uploadingKey} />
                    </label>
                    <button onClick={() => { loadStock(); loadInsights(); loadProjection(); loadSales(); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                </>}
            />
            <div style={{ padding: '18px 22px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FluidStatusTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

                {loading ? (
                    <div style={{ padding: '60px', textAlign: 'center', color: 'var(--grn-text-muted)', fontSize: '16px' }}>Loading...</div>
                ) : (
                    <>
                        {activeTab === 'dashboard' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <KpiBento items={[
                                    { label: 'Total Revenue', value: Number(insights?.total_revenue || 0), prefix: '₹' },
                                    { label: 'Avg Invoice Value', value: insights?.invoice_count ? Math.round(insights.total_revenue / insights.invoice_count) : 0, prefix: '₹' },
                                    { label: 'SOH Qty', value: totalStockQty },
                                    { label: 'SOH Value', value: totalStockValue, prefix: '₹' },
                                ]} columns="repeat(4,1fr)" />

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div style={cardS}>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--grn-text)', marginBottom: '16px' }}>Daily Net Sales <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(<strong>Days</strong> on X axis, <strong>₹</strong> on Y axis)</span></div>
                                        {insights?.daily_net_sales?.length > 0 ? (
                                            (() => {
                                                const maxV = Math.max(...insights.daily_net_sales.map(d => d.net_amount), 1);
                                                const minV = Math.min(...insights.daily_net_sales.map(d => d.net_amount), 0);
                                                const mid = Math.round((maxV + minV) / 2);
                                                const maxAbs = Math.max(...insights.daily_net_sales.map(d => Math.abs(d.net_amount)), 1);
                                                return (
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 52, height: 130, paddingBottom: 18 }}>
                                                            <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>₹{maxV.toLocaleString('en-IN')}</div>
                                                            <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>₹{mid.toLocaleString('en-IN')}</div>
                                                            <div style={{ fontSize: 9, color: '#000', fontWeight: 700, textAlign: 'right' }}>₹{minV.toLocaleString('en-IN')}</div>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 110, overflowX: 'auto' }}>
                                                                {insights.daily_net_sales.map((d) => {
                                                                    const pct = (Math.abs(d.net_amount) / maxAbs) * 100;
                                                                    return (
                                                                        <div key={d.sale_date} title={`${d.sale_date}: ₹${Number(d.net_amount).toLocaleString('en-IN')}`}
                                                                            style={{ flex: '0 0 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                                                                            <div style={{ width: '100%', height: `${Math.max(pct, 4)}%`, background: d.net_amount < 0 ? 'var(--grn-error)' : 'var(--grn-purple)', borderRadius: '3px 3px 0 0' }} />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                                                                {insights.daily_net_sales.map((d) => (
                                                                    <div key={d.sale_date} style={{ flex: '0 0 18px', textAlign: 'center' }}>
                                                                        <div style={{ fontSize: 9, color: '#000', fontWeight: 700 }}>{parseInt(d.sale_date.slice(8, 10), 10)}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div style={{ textAlign: 'center', color: 'var(--grn-text-muted)', padding: '30px' }}>No sales data yet</div>
                                        )}
                                    </div>

                                    <div style={cardS}>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--grn-text)', marginBottom: '14px' }}>Top Accounts <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(Sales)</span></div>
                                        {!insights?.top_accounts?.length ? (
                                            <div style={{ textAlign: 'center', color: 'var(--grn-text-muted)', padding: '20px' }}>No account data yet</div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                                                {insights.top_accounts.map((a, i) => (
                                                    <div key={i} style={{ background: 'var(--grn-surface-subtle)', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--grn-border)' }}>
                                                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--grn-text)', marginBottom: 6 }}>{a.account}</div>
                                                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--grn-purple)' }}>₹{Number(a.net_amount || 0).toLocaleString('en-IN')}</div>
                                                        <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 2 }}>{a.order_count} orders</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={cardS}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--grn-text)' }}>Top 10 SKUs <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(by Sales)</span></div>
                                        <button onClick={() => exportXLSX(
                                            ['Sr. No.', 'EAN', 'SKU Name', 'Net Qty', 'Net Revenue'],
                                            (insights?.top_skus || []).map((s, i) => [i + 1, s.ean || '', s.item_name, s.net_qty_sold, s.net_amount_sold]),
                                            `Choudhary_Top_SKUs_${today()}.xlsx`
                                        )} style={dlBtn}>⬇ Download</button>
                                    </div>
                                    {!insights?.top_skus?.length ? (
                                        <div style={{ textAlign: 'center', color: 'var(--grn-text-muted)', padding: '20px' }}>No SKU-wise sales data yet (only available with the newer per-SKU sales file)</div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={tableS}>
                                                <thead>
                                                    <tr>
                                                        {['Sr. No.', 'EAN', 'SKU Name', 'Net Qty', 'Net Revenue'].map(h => (
                                                            <th key={h} style={thS}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {insights.top_skus.map((s, i) => (
                                                        <tr key={i} style={{ background: i % 2 === 0 ? 'var(--grn-surface)' : 'var(--grn-surface-subtle)' }}>
                                                            <td style={td}>{i + 1}</td>
                                                            <td style={{ ...td, fontFamily: 'monospace', fontSize: '12px', color: 'var(--grn-purple)' }}>{s.ean || <span style={{ color: 'var(--grn-border-soft)' }}>—</span>}</td>
                                                            <td style={{ ...td, textAlign: 'left', fontWeight: 500, maxWidth: '240px' }}>{s.item_name}</td>
                                                            <td style={{ ...td, fontWeight: 700, color: s.net_qty_sold < 0 ? 'var(--grn-error)' : 'var(--grn-blue)' }}>{Number(s.net_qty_sold).toLocaleString('en-IN')}</td>
                                                            <td style={{ ...td, color: 'var(--grn-green)', fontWeight: 600 }}>₹{Number(s.net_amount_sold).toLocaleString('en-IN')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'projection' && (
                            <div>
                                <div style={{ ...cardS, marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
                                    <div>
                                        <div style={{ fontSize:'16px', fontWeight:700, color:'var(--grn-text)' }}>20-Day Inventory Projection</div>
                                        <div style={{ fontSize:'13px', color:'var(--grn-text-muted)', marginTop:'2px' }}>
                                            Based on avg daily net sales · Stock date: {formatDate(projection?.stock_date)}
                                        </div>
                                    </div>
                                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
                                        <button onClick={() => exportXLSX(
                                            ['EAN','SKU Name','Current Stock','Avg Daily Sales','Needed 20 Days','Gap','Days of Stock','Status'],
                                            filteredProj.map(r=>[r.ean||'',r.item_name,r.current_stock,r.avg_daily_sales,r.needed_20_days,r.gap,r.days_of_stock??'No Sales',r.status]),
                                            `Choudhary_Projection_${today()}.xlsx`
                                        )} style={dlBtn}>⬇ Download</button>
                                        <input type="text" placeholder="Search SKU..." style={inputS}
                                            value={projSearch} onChange={e => setProjSearch(e.target.value)} />
                                        {['all','NON_MOVING','CRITICAL','LOW','HEALTHY','OUT'].map(s => (
                                         <button key={s} onClick={() => setProjFilter(s)}
                                          style={{ padding:'7px 14px', borderRadius:'20px', border: projFilter===s?'none':'1.5px solid var(--grn-border)', background: projFilter===s?(STATUS_COLORS[s]?.bg||'var(--grn-accent-surface)'):'var(--grn-surface)', color: projFilter===s?(STATUS_COLORS[s]?.color||'#fff'):'var(--grn-text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                                          {s === 'all' ? 'All' : STATUS_COLORS[s]?.label || s}
                                           </button>
                                         ))}
                                    </div>
                                </div>

                                <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'12px', marginBottom:'16px' }}>
                                 {Object.entries(STATUS_COLORS).map(([status, cfg]) => {
                                  const count = projRows.filter(r => r.status === status).length;
                                 return (
                                   <div key={status} style={{ background:'var(--grn-surface)', borderRadius:'10px', padding:'14px 16px', boxShadow:'var(--grn-shadow-sm)', border:`2px solid ${cfg.bg}` }}>
                                     <div style={{ fontSize:'11px', fontWeight:700, color:cfg.color, textTransform:'uppercase', letterSpacing:'0.5px' }}>{cfg.label}</div>
                                     <div style={{ fontSize:'28px', fontWeight:800, color:cfg.color, marginTop:'4px' }}>{count}</div>
                                     <div style={{ fontSize:'11px', color:'var(--grn-text-muted)' }}>SKUs</div>
                                     </div>
                                     );
                                   })}
                                </div>

                                <div style={{ background:'var(--grn-surface)', borderRadius:'16px', boxShadow:'var(--grn-shadow-sm)', border:'1px solid var(--grn-border)', overflow:'hidden' }}>
                                    <div style={{ overflowX:'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['EAN','SKU Name','Current Stock','In Transit','Avg Daily Sales','Needed (20 Days)','Gap','Days of Stock','Status'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProj.length === 0 ? (
                                                    <tr><td colSpan={9} style={{ padding:'40px', textAlign:'center', color:'var(--grn-text-muted)' }}>No SKUs match filter</td></tr>
                                                ) : filteredProj.map((r, i) => {
                                                    const sc = STATUS_COLORS[r.status] || { bg:'var(--grn-surface-subtle)', color:'var(--grn-text-muted)' };
                                                    return (
                                                        <tr key={i} style={{ background: i%2===0?'var(--grn-surface)':'var(--grn-surface-subtle)' }}>
                                                            <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'var(--grn-purple)' }}>{r.ean || <span style={{color:'var(--grn-border-soft)'}}>—</span>}</td>
                                                            <td style={{ ...td, maxWidth:'220px', whiteSpace:'normal', fontWeight:500 }}>{r.item_name}</td>
                                                            <td style={{ ...td, fontWeight:700 }}>{Number(r.current_stock).toLocaleString('en-IN')}</td>
                                                            <td style={{ ...td, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${Number(r.in_transit_qty).toLocaleString('en-IN')}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                                                            <td style={{ ...td, color:'var(--grn-text)' }}>{r.avg_daily_sales}/day</td>
                                                            <td style={{ ...td, fontWeight:700, color:'var(--grn-blue)' }}>{r.needed_20_days}</td>
                                                            <td style={{ ...td, fontWeight:700, color: r.gap > 0 ? 'var(--grn-error)' : 'var(--grn-green)' }}>
                                                                {r.gap > 0 ? `+${r.gap} needed` : `${Math.abs(r.gap)} surplus`}
                                                            </td>
                                                            <td style={{ ...td, fontWeight:700 }}>
                                                                {r.days_of_stock === null ? '∞' : `${r.days_of_stock}d`}
                                                            </td>
                                                            <td style={td}>
                                                                <span style={{ fontSize:'11px', fontWeight:700, padding:'3px 10px', borderRadius:'5px', background:sc.bg, color:sc.color, whiteSpace:'nowrap' }}>
                                                                    {r.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'stock' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <KpiBento items={[
                                    { label: 'SOH Qty', value: totalStockQty },
                                    { label: 'SOH Amount', value: totalStockValue, prefix: '₹' },
                                    { label: 'Zero Stock SKUs', value: zeroStockSkus },
                                ]} columns="repeat(auto-fit, minmax(220px, 1fr))" />

                                <div style={cardS}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--grn-text)' }}>Stock Status</div>
                                        <input type="text" placeholder="Search item or EAN..." value={stockSearch} onChange={e => setStockSearch(e.target.value)}
                                            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--grn-border)', fontSize: '13px', width: '260px', fontFamily: FONT }} />
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['EAN', 'Item Name', 'Quantity', 'In Transit', 'Unit', 'Amount'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredStock.length === 0 ? (
                                                    <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: 'var(--grn-text-muted)' }}>No stock data</td></tr>
                                                ) : filteredStock.map((r, i) => (
                                                    <tr key={i} style={{ background: i % 2 === 0 ? 'var(--grn-surface)' : 'var(--grn-surface-subtle)' }}>
                                                        <td style={{ ...td, fontFamily: 'monospace', color: r.ean ? 'var(--grn-purple)' : 'var(--grn-error)' }}>{r.ean || 'Unmapped'}</td>
                                                        <td style={td}>{r.item_name}</td>
                                                        <td style={{ ...td, fontWeight: 700 }}>{Number(r.quantity).toLocaleString('en-IN')}</td>
                                                        <td style={{ ...td, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${Number(r.in_transit_qty).toLocaleString('en-IN')}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                                                        <td style={td}>{r.unit}</td>
                                                        <td style={{ ...td, color: 'var(--grn-green)', fontWeight: 600 }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'sales' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div style={cardS}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
                                        <div>
                                            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--grn-text)' }}>Sales ({filteredSales.length})</div>
                                            <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 2 }}>One row per invoice line (or per invoice, for older-format uploads).</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <input type="text" placeholder="Search item, bill no., or customer..." value={salesSearch} onChange={e => setSalesSearch(e.target.value)}
                                                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--grn-border)', fontSize: '13px', width: '280px', fontFamily: FONT }} />
                                            <button onClick={() => exportXLSX(
                                                ['Date', 'Bill No.', 'Item', 'EAN', 'Customer', 'Qty', 'Amount', 'Type'],
                                                filteredSales.map(r => [r.sale_date, r.vch_bill_no, r.item_name || '', r.ean || '', r.particulars, r.quantity ?? '', r.amount, r.is_credit_note ? 'Credit' : 'Sale']),
                                                `Choudhary_Sales_${today()}.xlsx`
                                            )} style={dlBtn}>⬇ Download</button>
                                        </div>
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['Date', 'Bill No.', 'Item', 'EAN', 'Customer', 'Qty', 'Amount', 'Type'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredSales.length === 0 ? (
                                                    <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: 'var(--grn-text-muted)' }}>No sales data uploaded yet</td></tr>
                                                ) : filteredSales.slice(0, 500).map((r, i) => (
                                                    <tr key={i} style={{ background: r.is_credit_note ? 'var(--grn-pink-tint)' : i % 2 === 0 ? 'var(--grn-surface)' : 'var(--grn-surface-subtle)' }}>
                                                        <td style={td}>{formatDate(r.sale_date)}</td>
                                                        <td style={td}>{r.vch_bill_no}</td>
                                                        <td style={{ ...td, textAlign: 'left' }}>{r.item_name || <span style={{ color: 'var(--grn-text-muted)' }}>—</span>}</td>
                                                        <td style={{ ...td, fontFamily: 'monospace', color: r.ean ? 'var(--grn-purple)' : 'var(--grn-text-muted)' }}>{r.ean || '—'}</td>
                                                        <td style={{ ...td, textAlign: 'left' }}>{r.particulars}</td>
                                                        <td style={td}>{r.quantity != null ? Number(r.quantity).toLocaleString('en-IN') : '—'}</td>
                                                        <td style={{ ...td, color: 'var(--grn-green)', fontWeight: 600 }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                                                        <td style={td}>
                                                            {r.is_credit_note
                                                                ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'var(--grn-pink-tint)', color: 'var(--grn-error)' }}>Credit</span>
                                                                : <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'var(--grn-green-tint)', color: 'var(--grn-green)' }}>Sale</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'sku_master' && (
                            <DistributorSkuMasterTable
                                nameCol="choudhary_name"
                                nameLabel="Choudhary Name"
                                canEdit={canEdit}
                                endpoint="/choudhary-inventory"
                                updatedBy={user?.username || 'Portal'}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const cardS = { background: 'var(--grn-surface)', borderRadius: '16px', padding: '20px', boxShadow: 'var(--grn-shadow-sm)', border: '1px solid var(--grn-border)' };
const dlBtn = { padding:'7px 16px', background:'var(--grn-accent-surface)', color:'#fff', border:'none', borderRadius:'7px', fontSize:'13px', fontWeight:600, cursor:'pointer' };
const inputS = { padding:'9px 14px', border:'1.5px solid var(--grn-border)', borderRadius:'8px', fontSize:'14px', outline:'none', width:'260px', fontFamily: FONT };
const tableS = { width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: FONT };
const thS = { background: 'var(--grn-accent-surface)', color: '#fff', padding: '10px 14px', textAlign: 'center', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '11px 14px', borderBottom: '1px solid var(--grn-border)', color: 'var(--grn-text)', verticalAlign: 'middle', textAlign: 'center' };
